<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs(campaign.title || $t('label.new_newsletter'))">
    <template #actions>
      <paywall-button
        :unlocked="isNewsletterFeatureAllowed"
        :disabled="!canSaveOrSend"
        color="success"
        @click="sendCampaign"
      >
        {{ $t('button.send_newsletter') }}
      </paywall-button>
      <v-btn
        :disabled="!canSaveOrSend || !isChanged"
        text
        @click="saveCampaign"
      >
        <v-icon class="mr-1">
          mdi-content-save-outline
        </v-icon>
        {{ $t('button.save_as_draft') }}
      </v-btn>
    </template>
    <template #header>
      <newsletter-quota-bar />
    </template>
    <template #content>
      <newsletter-test-email-recipients-dialog
        v-if="testRecipientsDialog"
        :recipients="testRecipients"
        @confirm="testCampaign($event)"
        @dismiss="testRecipientsDialog = false"
      />
      <newsletter-template-editor-dialog
        v-if="editorTemplate !== undefined"
        :template="editorTemplate"
        :template-locale="campaign.locale"
        :save-btn-text="$t('button.done')"
        @close="editorTemplate = undefined"
        @save="saveTemplate"
      />
      <contacts-filter-dialog
        v-if="contactFilterDialogVisible"
        :contact-filter="editedContactFilter"
        :visible="contactFilterDialogVisible"
        :match-contact-count="editedContactFilterMatchContactCount"
        @close="contactFilterDialogVisible = false"
        @save="saveContactFilter"
        @changed="changedContactFilter"
        @delete="deleteContactFilter"
      >
        <template #narrow-rules>
          <v-col
            class="py-0"
            cols="12"
          >
            <contacts-filter-value-row
              :value="{ field: 'newsletterSubscription', fieldType: 'boolean', id: 'dummy', type: 'is'}"
              :descriptor="ContactFilter.FilterDescriptor"
              disabled
            />
          </v-col>
        </template>
        <template #footer="{count}">
          <span
            v-if="count !== null"
            class="text-subtitle-2"
          >{{ $t('label.filter_matches_x_guests_with_newsletter', [count]) }}</span>
        </template>
      </contacts-filter-dialog>
      <paywall-upgrade-alert
        v-if="!isNewsletterFeatureAllowed"
        :text="$t('message.newsletter_trial_teaser')"
      />
      <validation-observer ref="refObserver">
        <settings-group
          gutter
          :title="$t('settings.newsletter.newsletter_settings')"
          :subtitle="$t('settings.newsletter.newsletter_settings_subtitle')"
        >
          <v-col
            cols="12"
            class="pt-4 pb-0"
          >
            <validated-text-field
              v-model="campaign.title"
              rules="required|max:255"
              class="my-2 w-100"
              :hint="$t('label.newsletter_name_hint')"
              :label="$t('label.newsletter_name')"
            />
          </v-col>
          <v-col
            cols="12"
            class="py-0 pb-2"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              required
            >
              <contact-filter-select
                v-model="campaign.contactFilter"
                :filters="contactFilters"
                allow-edit
                :error-messages="errors"
                :hint="contactFilterHintText"
                persistent-hint
                append-outer-icon="mdi-plus"
                :label="$t('label.audience')"
                class="mt-4"
                @add-filter="addContactFilter"
                @duplicate-filter="duplicateContactFilter"
                @edit-filter="editContactFilter"
                @input="selectContactFilter"
                @click:append-outer="addContactFilter"
              />
            </validation-provider>
          </v-col>
          <v-col
            cols="12"
            class="py-0"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              required
            >
              <v-select
                v-model="campaign.locale"
                :label="$t('label.language')"
                :items="languages"
                class="mt-2 pb-0"
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
        </settings-group>
        <settings-group
          gutter
          :title="$t('settings.newsletter.content')"
          :subtitle="$t('settings.newsletter.content_subtitle')"
        >
          <v-col
            cols="12"
            class="pt-4 pb-2"
          >
            <validated-text-field
              v-model="campaign.subject"
              rules="required|max:255"
              :label="$t('label.subject')"
            />
          </v-col>
          <v-col
            cols="12"
            class="py-0"
          >
            <validated-text-field
              v-model="campaign.previewText"
              rules="max:150"
              :label="$t('label.newsletter_email_precontent')"
              :hint="$t('label.newsletter_email_precontent_hint')"
            />
          </v-col>
          <v-col
            v-if="!template?.preview && campaignLoaded"
            cols="12"
            class="py-0"
          >
            <template-grid-view
              class="pt-2"
              :card-cols="6"
              :card-cols-sm="4"
              :card-cols-md="4"
              :card-cols-lg="3"
              :templates="templates"
              @select-template="selectTemplate"
              @edit-template="selectTemplate"
            >
              <template #title>
                <v-col
                  cols="12"
                  class="pb-0"
                >
                  <p class="subheading v-label mt-2 mb-0">
                    {{ $t('label.email_body') }}
                  </p>
                </v-col>
              </template>
            </template-grid-view>
          </v-col>
          <template v-if="template?.preview && campaignLoaded">
            <v-col
              cols="12"
              class="pb-2"
            >
              <p class="subheading mt-2 mb-0">
                {{ $t('label.newsletter_email_preview') }}
              </p>
            </v-col>
            <v-col
              cols="12"
              class="pt-0"
            >
              <email-preview-frame :subject="campaign.subject">
                <template #toolbar-items>
                  <v-btn
                    :unlocked="isNewsletterFeatureAllowed"
                    small
                    outlined
                    color="green"
                    @click="pickTestRecipients"
                  >
                    {{ $t('button.send_test_email') }}
                  </v-btn>
                  <v-spacer />
                  <v-btn
                    text
                    small
                    @click="editCampaignTemplate"
                  >
                    <v-icon
                      small
                      class="mr-2"
                    >
                      mdi-pencil
                    </v-icon>
                    {{ $t('button.edit') }}
                  </v-btn>
                  <v-btn
                    text
                    small
                    color="red"
                    @click="removeCampaignTemplate"
                  >
                    <v-icon
                      small
                      class="mr-2"
                    >
                      mdi-delete
                    </v-icon>
                    {{ $t('button.delete') }}
                  </v-btn>
                </template>
                <html-image :html="template.preview" />
              </email-preview-frame>
            </v-col>
          </template>
        </settings-group>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
import {
  ref, computed, onMounted, getCurrentInstance, onBeforeUnmount,
} from 'vue';

import HtmlImage from '@/components/util/HtmlImage.vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import {
  actionShowError, performDispatchAction, performSaveAction, performValidation,
} from '@/services/vue-utils';
import store from '@/store/store';
import ContactsFilterDialog from '@/components/views/contacts/ContactsFilterDialog.vue';
import { loadStoreModule, tryUnloadStoreModule, unloadStoreModule } from '@/services/module-loader';
import CampaignManagerStore from '@/store/marketing-campaign-manager-store';
import MarketingCampaign from '@/grpc-api/model/marketing-campaign';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import i18n from '@/plugins/i18n';
import useUnsavedGuard from '@/composables/unsaved-guard';
import { useRouter } from 'vue-router/composables';
import { ValidationObserver } from 'vee-validate';
import ContactFilter from '@/grpc-api/model/contact-filter';
import HtmlTemplate, { areHtmlTemplatesSame } from '@/grpc-api/model/html-template';
import nameWithCopyText from '@/util/copy-name';
import toast from '@/plugins/toast-plugin/toast';
import showConfirmationDialog from '@/components/dialogs/confirmation-dialog';
import ContactFilterSelect from '@/components/views/contacts/ContactFilterSelect.vue';
import { ApiErrorCode } from '@/api/api-error';
import PaywallButton from '@/components/buttons/PaywallButton.vue';
import PaywallUpgradeAlert from '@/components/dialogs/PaywallUpgradeAlert.vue';
import NewsletterQuotaBar from './NewsletterQuotaBar.vue';
import NewsletterTemplateEditorDialog from './NewsletterTemplateEditorDialog.vue';
import TemplateGridView from './TemplateGridView.vue';
import { useSortedLanguages } from '../settings/helpers/settings-values';
import EmailPreviewFrame from './EmailPreviewFrame.vue';
import NewsletterTestEmailRecipientsDialog from './NewsletterTestEmailRecipientsDialog.vue';
import ContactsFilterValueRow from '../contacts/ContactsFilterValueRow.vue';

const router = useRouter();

const isNewsletterFeatureAllowed = computed<boolean>(() => store.getters.isNewsletterFeatureAllowed);

const campaignLoaded = ref(false);
const campaign = ref(new MarketingCampaign());
const oldCampaign = ref(campaign.value);
const template = ref(undefined as undefined | HtmlTemplate);
const oldTemplate = ref(undefined as undefined | HtmlTemplate);
const editorTemplate = ref(undefined as undefined | HtmlTemplate);

const isChanged = computed(() => campaignLoaded.value
  && (
    !isModelEqualToModel(oldCampaign.value, campaign.value)
  || (oldTemplate.value !== undefined && template.value === undefined)
  || (oldTemplate.value === undefined && template.value !== undefined)
  || (
    oldTemplate.value !== undefined && template.value !== undefined
    && !areHtmlTemplatesSame(oldTemplate.value, template.value))
  ));
useUnsavedGuard(isChanged);
const canSaveOrSend = computed(() => campaignLoaded.value && template.value?.html !== undefined);

const templates = computed(() => store.getters.marketingCampaignManagerHtmlTemplates as HtmlTemplate[]);
const languages = useSortedLanguages();
const contactFilters = computed(() => store.getters.marketingCampaignManagerContactFilters as ContactFilter[]);

const editedContactFilter = ref(null as null | ContactFilter);
const contactFilterDialogVisible = ref(false);
const editedContactFilterMatchContactCount = ref(null as null | number);
const selectedContactFilterMatchContactCount = ref(null as null | number);

const contactFilterHintText = computed(() => {
  const { value } = selectedContactFilterMatchContactCount;
  return value !== null ? i18n.tc('label.x_recipients', value) : undefined;
});

// template grid actions
const selectTemplate = (t: HtmlTemplate) => {
  editorTemplate.value = t;
};

// campaign template actions
const editCampaignTemplate = () => {
  editorTemplate.value = template.value;
};

const removeCampaignTemplate = () => {
  const action = () => { template.value = undefined; };

  showConfirmationDialog(
    i18n.t('title.newsletter'),
    i18n.t('message.confirm_remove_template'),
    { action },
    {},
  );
};

// campaign actions
const refObserver = ref(null as null | InstanceType<typeof ValidationObserver>);
const savec = async (p = { hideSuccess: false }) => {
  if (!template.value) return false;

  campaign.value.templateName = template.value.name;
  campaign.value.templateDesign = template.value.design;
  campaign.value.templateHtml = template.value.html;
  campaign.value.templatePlaintext = template.value.plaintext;

  let savedCampaign = undefined as MarketingCampaign | undefined;
  const ok = await performSaveAction(
    refObserver.value,
    async () => { savedCampaign = await store.dispatch('updateMarketingCampaign', { campaign: campaign.value }); },
    p,
  );
  if (!ok || !savedCampaign) return false;

  campaign.value = savedCampaign;
  oldCampaign.value = cloneModel(campaign.value);
  oldTemplate.value = cloneModel(template.value);

  return true;
};

const saveCampaign = async () => {
  const ok = await savec();
  if (!ok) return;
  router.replace('/newsletter');
};

const sendCampaign = async () => {
  const valid = await performValidation(refObserver.value);
  if (!valid) return;

  const sendAction = async (omitCampaignCheck: boolean) => {
    const successText = i18n.tc('message.campaign_successfuly_sent');

    try {
      const sent = await performDispatchAction<MarketingCampaign>(
        'sendMarketingCampaign',
        { campaign: campaign.value, omitCampaignCheck },
        { successText, rethrow: true },
      );
      if (sent) router.replace('/newsletter');
    } catch (e: any) {
      if (!omitCampaignCheck && e.code === ApiErrorCode.campaign_manager_similar_campaign_found) {
        showConfirmationDialog(
          campaign.value.title ?? '',
          i18n.t('message.confirm_send_similar_campaign'),
          {
            title: i18n.tc('button.confirm', selectedContactFilterMatchContactCount.value ?? 0),
            action: () => sendAction(true),
          },
          {},
        );
      } else {
        actionShowError(e);
      }
    }
  };

  const action = async () => {
    const saved = await savec({ hideSuccess: true });
    if (saved) sendAction(false);
  };

  showConfirmationDialog(
    campaign.value.title ?? '',
    i18n.t('message.confirm_send_campaign', [selectedContactFilterMatchContactCount.value]),
    { title: i18n.tc('button.send_x_emails', selectedContactFilterMatchContactCount.value ?? 0), action },
    {},
  );
};

const testRecipients = computed(() => {
  const recipients = store.state.settings.users;
  return recipients.filter((u) => u.isRegistered).map((u) => ({ text: u.name, value: u.email! })) ?? [];
});

const testRecipientsDialog = ref(false);

const pickTestRecipients = async () => {
  const valid = await performValidation(refObserver.value);
  if (!valid) return;

  testRecipientsDialog.value = true;
};

const testCampaign = async (recipients: string[]) => {
  testRecipientsDialog.value = false;

  // const action = async () => {
  const saved = await savec({ hideSuccess: true });
  if (!saved) return;

  const send = await performDispatchAction('testMarketingCampaign', { campaign: campaign.value, recipients });
  if (!send) return;

  toast.success(i18n.tc('message.message_successfuly_sent'));
  // };
};

const loadCampaign = async () => {
  const route = router.currentRoute;

  let c: MarketingCampaign | undefined | null;

  // valid id route param? -> load campaign
  if (route.params.id !== 'new' && Number(route.params.id) > 0) {
    const id = Number(route.params.id) || 0;
    c = (store.getters.marketingCampaignManagerCampaigns as MarketingCampaign[]).find((o) => o.id === id);

    if (!c) {
      actionShowError(new Error(i18n.tc('error.load_data')));
      router.replace('/newsletter');
    }

    c = await performDispatchAction<MarketingCampaign>('getMarketingCampaign', { campaign: { id } });
    if (!c) {
      router.replace('/newsletter');
    }
  }

  if (c && route.query.action === 'copy') {
    // copy action -> make campaign duplicate
    oldCampaign.value = c;
    campaign.value = cloneModel(c);
    campaign.value.id = 0;
    campaign.value.title = nameWithCopyText(campaign.value.title ?? '', i18n.tc('label.copy_of'));
  } else if (c) {
    // valid campaign
    oldCampaign.value = c;
    campaign.value = cloneModel(c);
  } else {
    // new campaign
    oldCampaign.value = new MarketingCampaign();
    oldCampaign.value.locale = store.state.settings.account.locale;
    campaign.value = cloneModel(oldCampaign.value);
  }

  template.value = new HtmlTemplate({
    name: campaign.value.templateName ?? '',
    design: campaign.value.templateDesign ?? undefined,
    html: campaign.value.templateHtml ?? undefined,
    plaintext: campaign.value.templatePlaintext ?? undefined,
    preview: campaign.value.templatePreview ?? undefined,
  });
  oldTemplate.value = cloneModel(template.value);

  // eslint-disable-next-line no-use-before-define
  selectContactFilter(campaign.value.contactFilter ?? undefined);

  console.log('loadCampaign: ', campaign.value, template.value);
  campaignLoaded.value = true;
};

const saveTemplate = async (t: HtmlTemplate) => {
  template.value = t;
  editorTemplate.value = undefined;
};

// contact filter editor actions
const changedContactFilter = async (contactFilter: ContactFilter) => {
  const action = 'previewContactFilterForMarketingCampaign';
  try {
    const count = await store.dispatch(action, { contactFilter });
    editedContactFilterMatchContactCount.value = count ?? null;
  } catch (e) {
    editedContactFilterMatchContactCount.value = null;
  }
  console.log('changedContactFilter: ', editedContactFilterMatchContactCount.value);
};

const saveContactFilter = async (contactFilter: ContactFilter) => {
  const action = contactFilter.id === 0
    ? 'markeingCampaignManagerCreateContactFilter'
    : 'markeingCampaignManagerUpdateContactFilter';
  const changedFilter = await performDispatchAction<ContactFilter>(action, { contactFilter });
  if (!changedFilter) return;

  contactFilterDialogVisible.value = false;

  campaign.value.contactFilter = changedFilter;
  const res = await performDispatchAction<number>('previewContactFilterForMarketingCampaign', { contactFilter });
  selectedContactFilterMatchContactCount.value = res ?? null;
};

const deleteContactFilter = async (contactFilter: ContactFilter) => {
  const action = 'markeingCampaignManagerDeleteContactFilter';
  const changedFilter = await performDispatchAction<ContactFilter>(action, { contactFilter });
  if (!changedFilter) return;

  contactFilterDialogVisible.value = false;

  campaign.value.contactFilter = null;
  selectedContactFilterMatchContactCount.value = null;
};

// contact filter selector actions
const selectContactFilter = async (contactFilter?: ContactFilter) => {
  selectedContactFilterMatchContactCount.value = null;
  if (contactFilter?.data === undefined) return;

  campaign.value.contactFilter = contactFilter;
  const res = await performDispatchAction<number>('previewContactFilterForMarketingCampaign', { contactFilter });
  selectedContactFilterMatchContactCount.value = res ?? null;
};

const addContactFilter = () => {
  editedContactFilter.value = null;
  contactFilterDialogVisible.value = true;
};

const duplicateContactFilter = () => {
  if (!campaign.value.contactFilter) return;
  editedContactFilter.value = cloneModel(campaign.value.contactFilter);
  editedContactFilter.value.id = 0;
  editedContactFilter.value.name = nameWithCopyText(campaign.value.contactFilter.text, i18n.tc('label.copy_of'));
  contactFilterDialogVisible.value = true;
};

const editContactFilter = () => {
  editedContactFilter.value = campaign.value.contactFilter;
  contactFilterDialogVisible.value = true;
};

// view actions
const load = async () => {
  if (!store.getters.isMarketingCampaignManagerLoaded) {
    await performDispatchAction('loadMarketingCampaignManager', {});
  }
  loadCampaign();
};

const reset = async () => {
  store.dispatch('resetMarketingCampaignManager');
  tryUnloadStoreModule('serviceManager', async () => store.dispatch('resetServiceManager'));
};

onMounted(() => {
  loadStoreModule(
    getCurrentInstance()?.proxy!,
    'marketingCampaignManager',
    CampaignManagerStore,
    async () => { load(); },
  );
});

onBeforeUnmount(() => {
  unloadStoreModule(getCurrentInstance()?.proxy!, 'marketingCampaignManager', async () => { reset(); });
});

</script>
